import { useState, useRef } from 'react'
import { collection, addDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { CATEGORIES } from '../../lib/categories'
import './AdminEditor.css'

// Category mapping from old format to new
const CATEGORY_MAP = {
  'party': 'campaign',
  'world': 'world',
  'discovery': 'discovery',
  'travel': 'travel',
  'critical': 'battle',
  'covert': 'political'
}

// The events from campaign-timeline (built-in)
const EXISTING_EVENTS = [
  {
    title: "The Party Forms in Neverwinter",
    start: { month: "Mirtul", day: 15, year: 1492 },
    description: "A chance meeting at the Driftwood Tavern brings together unlikely allies.",
    category: "campaign",
    location: "Neverwinter"
  },
  {
    title: "Discovery of the Duskryn Sigil",
    start: { month: "Kythorn", day: 3, year: 1492 },
    description: "An ancient drow house seal found in the ruins beneath Neverwinter.",
    category: "discovery",
    location: "Neverwinter Ruins"
  },
  {
    title: "Midsummer Festival",
    start: { month: "Midsummer", day: 1, year: 1492 },
    description: "The party witnesses strange omens during the festival celebrations.",
    category: "other",
    location: "Neverwinter"
  },
  {
    title: "Journey to Waterdeep",
    start: { month: "Eleasis", day: 1, year: 1492 },
    end: { month: "Eleasis", day: 18, year: 1492 },
    description: "Travel along the High Road, encountering bandits and a mysterious caravan.",
    category: "other",
    location: "High Road"
  },
  {
    title: "The Blackstaff's Warning",
    start: { month: "Eleasis", day: 22, year: 1492 },
    description: "Vajra Safahr reveals ancient prophecies tied to House Duskryn.",
    category: "political",
    location: "Blackstaff Tower, Waterdeep"
  },
  {
    title: "Betrayal at the Yawning Portal",
    start: { month: "Eleint", day: 5, year: 1492 },
    description: "An ally reveals their true allegiance. Blood is spilled.",
    category: "battle",
    location: "Yawning Portal, Waterdeep"
  },
  {
    title: "Descent into Undermountain",
    start: { month: "Eleint", day: 7, year: 1492 },
    end: { month: "Marpenoth", day: 2, year: 1492 },
    description: "Nearly a month in the depths of Halaster's domain.",
    category: "campaign",
    location: "Undermountain"
  },
  {
    title: "Recovery in Waterdeep",
    start: { month: "Marpenoth", day: 3, year: 1492 },
    end: { month: "Marpenoth", day: 20, year: 1492 },
    description: "Healing wounds and deciphering artifacts recovered from the depths.",
    category: "campaign",
    location: "Waterdeep"
  },
  {
    title: "The Duskryn Codex Translated",
    start: { month: "Uktar", day: 8, year: 1492 },
    description: "The ancient text reveals coordinates pointing to Turmish.",
    category: "discovery",
    location: "Waterdeep"
  },
  {
    title: "Feast of the Moon - Vision",
    start: { month: "Feast of the Moon", day: 1, year: 1492 },
    description: "During the night of remembrance, the dead speak warnings.",
    category: "other",
    location: "Waterdeep"
  },
  {
    title: "Sea Voyage to Turmish",
    start: { month: "Nightal", day: 2, year: 1493 },
    end: { month: "Kythorn", day: 1, year: 1494 },
    description: "Six months at sea, stopping at ports along the way. Storms, pirates, and strange waters.",
    category: "other",
    location: "Sea of Fallen Stars"
  },
  {
    title: "Arrival in Alaghôn",
    start: { month: "Kythorn", day: 1, year: 1494 },
    description: "First steps in Turmish. The air smells of jasmine and intrigue.",
    category: "campaign",
    location: "Alaghôn, Turmish"
  },
  {
    title: "Meeting Sofia of Pel -o Ingolé",
    start: { month: "Kythorn", day: 15, year: 1494 },
    description: "The noblewoman in mourning black has her own agenda—and information.",
    category: "discovery",
    location: "Alaghôn, Turmish"
  },
  {
    title: "The Summer Solstice Ritual",
    start: { month: "Kythorn", day: 20, year: 1494 },
    description: "Ancient powers stir. The boundary between worlds grows thin.",
    category: "battle",
    location: "Emerald Grove, Turmish"
  }
]

export default function MigrateEvents() {
  const [migrating, setMigrating] = useState(false)
  const [results, setResults] = useState([])
  const [done, setDone] = useState(false)
  const [customEvents, setCustomEvents] = useState(null)
  const [parseError, setParseError] = useState(null)
  const fileInputRef = useRef(null)

  const importEvents = async (events) => {
    setMigrating(true)
    setResults([])
    setDone(false)

    const eventsCollection = collection(db, 'events')

    for (const event of events) {
      try {
        // Normalize the event data
        const normalizedEvent = {
          title: event.title || 'Untitled Event',
          description: event.description || '',
          category: CATEGORY_MAP[event.category] || event.category || 'other',
          location: event.location || '',
          start: event.start,
          end: event.end || null,
          characterIds: event.characterIds || [],
          createdAt: new Date(),
          updatedAt: new Date()
        }

        const docRef = await addDoc(eventsCollection, normalizedEvent)
        setResults(prev => [...prev, { success: true, title: event.title, id: docRef.id }])
      } catch (error) {
        setResults(prev => [...prev, { success: false, title: event.title, error: error.message }])
      }
    }

    setMigrating(false)
    setDone(true)
  }

  const handleMigrateBuiltIn = async () => {
    if (!window.confirm('This will import 14 events from campaign-timeline. Continue?')) {
      return
    }
    await importEvents(EXISTING_EVENTS)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    setParseError(null)
    setCustomEvents(null)

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result)

        // Support both { events: [...] } and direct array format
        const events = Array.isArray(json) ? json : json.events

        if (!Array.isArray(events)) {
          throw new Error('JSON must be an array of events or have an "events" property')
        }

        if (events.length === 0) {
          throw new Error('No events found in file')
        }

        // Validate each event has at least a title and start date
        for (let i = 0; i < events.length; i++) {
          if (!events[i].title) {
            throw new Error(`Event ${i + 1} is missing a title`)
          }
          if (!events[i].start) {
            throw new Error(`Event "${events[i].title}" is missing a start date`)
          }
        }

        setCustomEvents(events)
      } catch (err) {
        setParseError(err.message)
      }
    }
    reader.readAsText(file)
  }

  const handleImportCustom = async () => {
    if (!customEvents) return
    if (!window.confirm(`Import ${customEvents.length} events from file?`)) {
      return
    }
    await importEvents(customEvents)
    setCustomEvents(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Import Timeline Events</h1>
      </div>

      {/* JSON File Import */}
      <section className="import-section">
        <h2>Import from JSON File</h2>
        <p className="text-secondary mb-lg">
          Upload a JSON file with your events. Format should be an array of events or {"{"}"events": [...]{"}"}.
        </p>

        <div className="import-format-help">
          <details>
            <summary>View expected JSON format</summary>
            <pre>{`{
  "events": [
    {
      "title": "Event Title",
      "start": { "month": "Mirtul", "day": 15, "year": 1492 },
      "end": { "month": "Mirtul", "day": 20, "year": 1492 },
      "description": "What happened...",
      "category": "campaign",
      "location": "Neverwinter"
    }
  ]
}

Categories: ${CATEGORIES.map(c => c.id).join(', ')}
Months: Hammer, Alturiak, Ches, Tarsakh, Mirtul, Kythorn,
        Flamerule, Eleasis, Eleint, Marpenoth, Uktar, Nightal
Special days: Midwinter, Greengrass, Midsummer, Shieldmeet,
              Highharvesttide, Feast of the Moon`}</pre>
          </details>
        </div>

        <div className="file-import-row">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileSelect}
            disabled={migrating}
          />
          {customEvents && (
            <button
              className="btn btn-primary"
              onClick={handleImportCustom}
              disabled={migrating}
            >
              {migrating ? 'Importing...' : `Import ${customEvents.length} Events`}
            </button>
          )}
        </div>

        {parseError && (
          <div className="error-banner mt-md">{parseError}</div>
        )}

        {customEvents && (
          <div className="preview-events mt-md">
            <h4>Preview ({customEvents.length} events)</h4>
            <ul>
              {customEvents.slice(0, 5).map((e, i) => (
                <li key={i}>{e.title} - {e.start?.month} {e.start?.day}, {e.start?.year}</li>
              ))}
              {customEvents.length > 5 && <li>...and {customEvents.length - 5} more</li>}
            </ul>
          </div>
        )}
      </section>

      {/* Built-in Migration */}
      <section className="import-section">
        <h2>Import Built-in Events</h2>
        <p className="text-secondary mb-lg">
          Import the 14 pre-configured events from the campaign-timeline project.
        </p>

        <button
          className="btn btn-secondary"
          onClick={handleMigrateBuiltIn}
          disabled={migrating}
        >
          {migrating ? 'Importing...' : 'Import 14 Built-in Events'}
        </button>
      </section>

      {/* Results */}
      {done && (
        <div className="success-banner mb-lg">
          Import complete! You can now view and edit events in the Events admin.
        </div>
      )}

      {results.length > 0 && (
        <div className="migration-results">
          <h3>Results</h3>
          <div className="admin-list">
            {results.map((result, i) => (
              <div key={i} className={`migration-item ${result.success ? 'success' : 'error'}`}>
                <span>{result.success ? '✓' : '✗'}</span>
                <span>{result.title}</span>
                {result.error && <span className="error-text">{result.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
